import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Building2, Users, Truck } from "lucide-react";
import { useMyTenants } from "../api/customerRelationships";

const TYPE_ICON = { forwarder: Building2, customer: Users, carrier: Truck };

// Google-style multi-tenant switcher. Hidden when the user has ≤1 accessible
// tenant. The active tenant is identified by `user.tenant_id`; switching
// stores the chosen id in localStorage under `sitroom_active_tenant_id` —
// today the backend still keys everything off the JWT's tenant_id (single-
// tenant pilot), but the FE backbone is in place for Phase B when users
// gain multi-tenant memberships.
export const ViewSwitcher = ({ currentTenantId }) => {
  const { data } = useMyTenants();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const items = data?.items || [];
  // Hide entirely when user has only 1 tenant — keeps the topbar quiet for
  // pilot users. Confirmed harmless per Phase A spec.
  if (items.length <= 1) return null;

  const active = items.find((t) => t.id === currentTenantId) || items[0];
  const ActiveIcon = TYPE_ICON[active.tenant_type] || Building2;

  const onPick = (t) => {
    setOpen(false);
    try {
      localStorage.setItem("sitroom_active_tenant_id", t.id);
    } catch (_) {}
    // Soft reload — the JWT determines the effective tenant in Phase A, so
    // the switch is informational today. Phase B will exchange the token
    // for the new tenant context.
    window.dispatchEvent(new CustomEvent("sitroom:tenant-switched", { detail: t }));
    window.location.reload();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md transition-colors hover:bg-white/[0.05]"
        style={{
          padding: "5px 9px",
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.10)",
          color: "#cfe1f6",
          fontFamily: "DM Mono, monospace",
          fontSize: 10,
          letterSpacing: "0.8px",
          cursor: "pointer",
          maxWidth: 200,
        }}
        data-testid="view-switcher-trigger"
        title="Switch tenant view"
        aria-label="Switch tenant"
      >
        <ActiveIcon size={12} />
        <span
          className="truncate"
          style={{ maxWidth: 130 }}
          data-testid="view-switcher-active-name"
        >
          {active.name}
        </span>
        <ChevronDown size={11} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 rounded-md"
          style={{
            width: 260,
            maxHeight: 360,
            overflowY: "auto",
            background: "#0d0f18",
            border: "1px solid rgba(255,255,255,0.10)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            zIndex: 400,
          }}
          data-testid="view-switcher-menu"
        >
          <div
            className="px-3 py-2"
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 9,
              color: "#9198b0",
              letterSpacing: "1.4px",
              textTransform: "uppercase",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            Switch tenant view
          </div>
          {items.map((t) => {
            const Icon = TYPE_ICON[t.tenant_type] || Building2;
            const isActive = t.id === active.id;
            return (
              <button
                key={t.id}
                onClick={() => onPick(t)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 transition-colors"
                style={{
                  background: isActive ? "rgba(24,95,165,0.15)" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.04)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = isActive ? "rgba(24,95,165,0.15)" : "transparent")}
                data-testid={`view-switcher-item-${t.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Icon size={13} color={isActive ? "#79b5f0" : "#9aa1b3"} />
                  <div className="min-w-0">
                    <div
                      className="truncate"
                      style={{
                        fontFamily: "Syne, sans-serif",
                        fontSize: 12,
                        color: "#f0f2f8",
                        fontWeight: 500,
                      }}
                    >
                      {t.name}
                    </div>
                    <div
                      style={{
                        fontFamily: "DM Mono, monospace",
                        fontSize: 9,
                        color: "#6b7388",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                      }}
                    >
                      {t.tenant_type}
                    </div>
                  </div>
                </div>
                {isActive && <Check size={13} color="#4ed084" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ViewSwitcher;
